import { useEffect, useMemo, useState } from 'react';
import { CompanyWithSitesMinifiedRowType } from '@api';
import { useCombobox } from '@mantine/core';
import {
  ADD_COMPANY_OPTION,
  ADD_COMPANY_SITE_OPTION,
  CompanySiteOptionType,
} from '../../common';
import { getCompanySitesOptions, getFilteredOptions } from './helpers';

type UseSiteComboboxArgsType = {
  companySites: CompanyWithSitesMinifiedRowType[];
  preselectSiteId?: string | null;
  handleAddCompany: () => void;
  handleAddCompanySite: (companyId: string) => void;
  selectedSiteId: string;
  handleSiteIdChange: (value: string) => void;
};

type UseSiteComboboxReturnType = {
  combobox: ReturnType<typeof useCombobox>;
  options: CompanySiteOptionType[];
  shouldShowAddCompany: boolean;
  shouldShowAddCompanySite: boolean;
  search: string;
  handleOptionSubmit: (value: string) => void;
  onSearchChange: (value: string) => void;
  onClear: () => void;
  showClearButton: boolean;
};

export const useSiteCombobox = ({
  companySites,
  selectedSiteId,
  preselectSiteId,
  handleAddCompany,
  handleAddCompanySite,
  handleSiteIdChange,
}: UseSiteComboboxArgsType): UseSiteComboboxReturnType => {
  const combobox = useCombobox();
  const { companySiteOptions } = useMemo(
    () => getCompanySitesOptions(companySites),
    [companySites]
  );
  const [search, setSearch] = useState(
    companySiteOptions.find(({ value }) => value === selectedSiteId)?.label ??
      ''
  );

  const {
    filteredOptions,
    shouldShowAddCompany,
    shouldShowAddCompanySite,
    singleCompanyId,
  } = getFilteredOptions(companySiteOptions, search);

  useEffect(() => {
    if (!preselectSiteId) return;

    const option = companySiteOptions.find(
      ({ value }) => value === preselectSiteId
    );

    if (!option) return;

    handleSiteIdChange(preselectSiteId);
    setSearch(option.label);
  }, [preselectSiteId, companySiteOptions, handleSiteIdChange]);

  const handleOptionSubmit = (submittedValue: string) => {
    switch (submittedValue) {
      case ADD_COMPANY_OPTION:
        handleAddCompany();
        combobox.closeDropdown();
        return;
      case ADD_COMPANY_SITE_OPTION: {
        if (singleCompanyId) {
          handleAddCompanySite(singleCompanyId);
        }
        combobox.closeDropdown();
        return;
      }
      default: {
        const option = companySiteOptions.find(
          ({ value }) => value === submittedValue
        );
        if (!option) return;

        setSearch(option.label);
        handleSiteIdChange(submittedValue);
        combobox.closeDropdown();
      }
    }
  };

  const onSearchChange = (value: string) => {
    combobox.openDropdown();
    combobox.updateSelectedOptionIndex();
    setSearch(value);
  };

  const onClear = () => {
    setSearch('');
    handleSiteIdChange('');
    combobox.closeDropdown();
  };

  const showClearButton = search.length > 0 || selectedSiteId !== '';

  return {
    combobox,
    options: filteredOptions,
    shouldShowAddCompany,
    shouldShowAddCompanySite,
    handleOptionSubmit,
    search,
    onSearchChange,
    onClear,
    showClearButton,
  };
};
